import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchFscHouseholdsQuery } from '../../app/api';

/* Search the FSC household cache and pick one.
 *
 * Shared by the new-client wizard and the client detail page — both are
 * answering the same question ("which existing AUM client is this?") and a
 * second copy would drift. Reads GET /fsc/households, which is office-scoped
 * on the server, so an advisor only ever searches their own book.
 *
 * Searches on a debounce rather than per keystroke: the endpoint is a LIKE
 * across 10,034 rows and the operator is usually typing a whole surname.
 */

const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  hairlineStrong: '#c4cbd4',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
  green:     '#1a7f4e',
  red:       '#b00020',
};

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

export default function FscHouseholdPicker({ onSelect, onCancel, autoFocus, busyForId }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const ready = debounced.length >= MIN_QUERY;
  const { data, isFetching, isError } = useSearchFscHouseholdsQuery(
    { q: debounced, limit: 12, letter_field: 'last_name' },
    { skip: !ready },
  );
  const results = data?.households || [];
  const total = data?.total || 0;

  const inputStyle = {
    width: '100%', border: `1px solid ${work.hairline}`, borderRadius: 4,
    padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink,
    background: 'white', outline: 'none',
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search clients by household, advisor or office…"
          style={{ ...inputStyle, flex: '1 1 260px', minWidth: 220 }}
        />
        {onCancel && (
          <button type="button" onClick={onCancel} style={{
            background: 'transparent', color: work.deepBlue,
            border: `1px solid ${work.hairlineStrong}`, borderRadius: 4,
            padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
          }}>
            Cancel
          </button>
        )}
      </div>

      {!ready ? (
        <p style={{ color: work.muted, fontSize: '0.78rem', margin: '0.5rem 0 0' }}>
          Type at least {MIN_QUERY} characters. Searches the Salesforce household cache - FSC labels
          households by first name, so a surname works best.
        </p>
      ) : isError ? (
        <p style={{ color: work.red, fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
          Could not search the household cache.
        </p>
      ) : isFetching ? (
        <p style={{ color: work.muted, fontSize: '0.8rem', margin: '0.5rem 0 0' }}>Searching…</p>
      ) : results.length === 0 ? (
        <p style={{ color: work.muted, fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
          No household matches &ldquo;{debounced}&rdquo;. Only households at your offices are searched.
        </p>
      ) : (
        <div style={{
          marginTop: '0.6rem', border: `1px solid ${work.hairline}`, borderRadius: 4,
          overflow: 'hidden', maxHeight: 320, overflowY: 'auto',
        }}>
          {results.map(h => {
            const inFlight = busyForId === h.fsc_id;
            return (
              <div
                key={h.fsc_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
                  padding: '0.6rem 0.8rem', borderBottom: `1px solid ${work.hairline}`,
                  background: 'white',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.88rem' }}>
                      {h.name || h.fsc_id}
                    </span>
                    {/* Already has portal logins attached — linking here would
                        be refused by the server, so say so before the click. */}
                    {h.has_portal && (
                      <span style={{
                        background: work.tealBlue, color: 'white', borderRadius: 4,
                        padding: '0.05rem 0.35rem', fontSize: '0.62rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                      }}>
                        Portal
                      </span>
                    )}
                  </div>
                  <div style={{
                    color: work.muted, fontSize: '0.76rem', marginTop: 1,
                    display: 'flex', flexWrap: 'wrap', gap: '0.2rem 0.9rem',
                  }}>
                    {h.rep_name && <span><i className="bi bi-person-badge me-1" />{h.rep_name}</span>}
                    {h.rep_firm_name && <span><i className="bi bi-building me-1" />{h.rep_firm_name}</span>}
                    {h.doc_count > 0 && <span><i className="bi bi-file-earmark-text me-1" />{h.doc_count}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onSelect(h)}
                  disabled={inFlight}
                  style={{
                    flexShrink: 0,
                    background: h.has_portal ? work.hairline : work.goldenrod,
                    color: h.has_portal ? work.muted : work.deepBlue,
                    border: 0, borderRadius: 4, padding: '0.35rem 0.8rem',
                    fontWeight: 700, fontSize: '0.8rem',
                    cursor: inFlight ? 'wait' : 'pointer',
                  }}
                >
                  {inFlight ? 'Linking…' : 'Link'}
                </button>
              </div>
            );
          })}
          {total > results.length && (
            <div style={{ padding: '0.45rem 0.8rem', color: work.muted, fontSize: '0.75rem', background: work.canvas }}>
              Showing {results.length} of {total} - narrow the search to see the rest.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
FscHouseholdPicker.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  autoFocus: PropTypes.bool,
  // fsc_id currently being linked, so its row can show progress.
  busyForId: PropTypes.string,
};

// Money as the Work side shows it elsewhere: whole dollars, grouped.
export const formatUsd = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
};

export const formatDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export { work as fscPickerPalette };
